import * as THREE from 'three';
import type { Landmark, NormalizedLandmark } from '@mediapipe/tasks-vision';
import type { HandSample, PoseSample } from './poseEstimation';

/** MediaPipe Pose の主要ランドマークのインデックス（33点中）。 */
const LEFT_SHOULDER = 11;
const RIGHT_SHOULDER = 12;
const LEFT_HIP = 23;
const RIGHT_HIP = 24;
const LANDMARK_COUNT = 33;

/** トリム平均で上下それぞれ切り捨てる割合。外れ値1フレームへの過剰反応を防ぐ。 */
const TRIM_RATIO = 0.2;
/** これ未満のビュー数ではトリムせず全件を使う（切り捨てるとサンプルが枯れるため）。 */
const MIN_VIEWS_FOR_TRIM = 5;

export interface FusedPose {
  /** 複数ビューを整列・加重平均した world landmarks（33点）。 */
  worldLandmarks: Landmark[];
  /** 基準ビュー（最も検出品質が高かったフレーム）の 2D landmarks。Kalidokit 用。 */
  refLandmarks2d: NormalizedLandmark[];
  /** 基準ビューの画像サイズ。Kalidokit 用。 */
  imageSize: { width: number; height: number };
  /**
   * 入力サンプルの中で handedness スコアが最良だった左手（あれば）。body と異なり
   * 複数ビューを融合せず、静止ポーズには最良1フレームで十分という判断で単純に選ぶ。
   */
  leftHand?: HandSample;
  /** 入力サンプルの中で handedness スコアが最良だった右手（あれば）。 */
  rightHand?: HandSample;
}

/** samples から指定サイドの handedness スコアが最大の HandSample を選ぶ。無ければ undefined。 */
function selectBestHand(samples: PoseSample[], side: 'leftHand' | 'rightHand'): HandSample | undefined {
  let best: HandSample | undefined;
  for (const s of samples) {
    const hand = s[side];
    if (hand && (!best || hand.score > best.score)) best = hand;
  }
  return best;
}

/** ランドマーク列の平均 visibility（0〜1）。基準ビュー選定や静止区間検出に使う。 */
function averageVisibility(landmarks: { visibility?: number }[]): number {
  let sum = 0;
  for (const lm of landmarks) sum += lm.visibility ?? 0;
  return sum / landmarks.length;
}

function toVec3(lm: Landmark): THREE.Vector3 {
  return new THREE.Vector3(lm.x, lm.y, lm.z);
}

/**
 * 胴体（肩・腰）の位置から、そのビューの姿勢を表す正規直交フレームを作る。
 * right: 体の右方向 / up: 体の上方向 / forward: 体の前方向。
 * world landmarks は腰中心が原点のメートル座標なので、姿勢の整列には
 * この3x3回転行列だけを使えばよい（平行移動は不要）。
 */
function computeTorsoFrame(landmarks: Landmark[]): THREE.Matrix3 {
  const lShoulder = toVec3(landmarks[LEFT_SHOULDER]);
  const rShoulder = toVec3(landmarks[RIGHT_SHOULDER]);
  const lHip = toVec3(landmarks[LEFT_HIP]);
  const rHip = toVec3(landmarks[RIGHT_HIP]);

  const shoulderRight = new THREE.Vector3().subVectors(lShoulder, rShoulder);
  const hipRight = new THREE.Vector3().subVectors(lHip, rHip);
  const right = new THREE.Vector3().addVectors(shoulderRight, hipRight).normalize();

  const shoulderCenter = new THREE.Vector3().addVectors(lShoulder, rShoulder).multiplyScalar(0.5);
  const hipCenter = new THREE.Vector3().addVectors(lHip, rHip).multiplyScalar(0.5);
  const up = new THREE.Vector3().subVectors(shoulderCenter, hipCenter).normalize();

  const forward = new THREE.Vector3().crossVectors(right, up).normalize();
  // up と forward から right を再直交化し、誤差を均す。
  const rightOrtho = new THREE.Vector3().crossVectors(up, forward).normalize();

  const m = new THREE.Matrix3();
  m.set(
    rightOrtho.x, up.x, forward.x,
    rightOrtho.y, up.y, forward.y,
    rightOrtho.z, up.z, forward.z,
  );
  return m;
}

/**
 * world landmarks を、そのフレームの胴体フレーム基準のローカル座標に変換する。
 * computeTorsoFrame が返す行列は正規直交（列が right/up/forward）なので、
 * その転置＝逆行列を world landmarks に掛けるだけで body 座標が得られる。
 * この座標系は「被写体からどう見えるか」を表すため、カメラが被写体の周りを
 * 回っても値が変わらず、被写体自身がポーズを崩したときだけ動く。
 */
function computeBodyLocalLandmarks(worldLandmarks: Landmark[]): THREE.Vector3[] {
  const frameT = computeTorsoFrame(worldLandmarks).transpose();
  return worldLandmarks.map((lm) => toVec3(lm).applyMatrix3(frameT));
}

/** 値の中央値。外れ値（誤検出フレーム1枚などの突発的な動き）に平均より強い。 */
function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

/**
 * 「一周撮影」でシード窓を伸長する際、隣接遷移の動き量がシード窓の
 * 中央値の何倍までなら同じ静止区間とみなすか。中央値そのものだと厳しすぎて
 * ノイズだけで窓が伸びなくなるため、ある程度の余裕を持たせる。
 */
const EXPANSION_MOTION_FACTOR = 2.5;

/**
 * 動画のうち「被写体がポーズを構えて静止している区間」だけを融合対象として選び出す。
 * ポーズを構えるまでの動きや崩す瞬間のフレームを融合に混ぜないための前処理。
 *
 * 動き量は landmarks2d（画像正規化座標）ではなく、body 座標系に正規化した
 * worldLandmarks（computeBodyLocalLandmarks）の連続フレーム間ユークリッド距離を、
 * 両フレームの visibility の積で重み付け平均して求める。2D の見かけの動きは
 * 「被写体が動いた」のか「カメラが動いた」のかを区別できず、被写体の周りを
 * カメラで回る撮り方（多視点にするほど奥行き推定が良くなる）では被写体が
 * 静止していてもカメラの動きがそのまま動き量として出てしまう。body 座標に
 * 正規化すればカメラの動きは打ち消され、被写体自身の動きだけが残る。
 *
 * 区間選定は2段階。まず固定幅の窓（seedSize）でスコア最良の区間を「シード」として
 * 見つけ、次にシードの前後へ、隣接遷移の動き量がシード窓の動き量の中央値の
 * EXPANSION_MOTION_FACTOR 倍以下である限り伸ばす。全編静止している一周撮影では
 * 窓が全サンプルまで伸びて多視点をすべて活かせる一方、途中でポーズを崩す動画では
 * 保持区間だけで伸長が止まる。
 *
 * シード探索のスコアは、窓内の平均動き量ができるだけ小さく、かつ窓内の平均
 * visibility ができるだけ高い区間を選ぶよう、両者をそれぞれ 0〜1 に正規化して
 * 動き量の少なさを 0.7、visibility の高さを 0.3 の重みで線形合成する
 * （静止しているかどうかの判定を主目的とするため動き量を優先）。
 *
 * サンプルが少ない、またはどの区間も動き量に有意な差がない（動き量の変動係数が小さい）
 * 場合は、静止区間検出が裏目に出て入力が減りすぎないよう全サンプルをそのまま返す。
 * world landmarks はメートル単位なので動き量もメートル単位になるが、変動係数や
 * 正規化スコア（motion/maxMotion）はスケール不変なので閾値はそのまま使える。
 */
export function selectStillWindow(samples: PoseSample[]): PoseSample[] {
  if (samples.length < 5) return samples;

  const seedSize = Math.max(5, Math.floor(samples.length / 3));
  if (seedSize >= samples.length) return samples;

  // 各サンプルの body 座標ローカル landmarks を一度だけ計算してキャッシュする。
  const bodyLocals = samples.map((s) => computeBodyLocalLandmarks(s.worldLandmarks));

  // フレーム i と i+1 の間の動き量（body 座標系）。要素数は samples.length - 1。
  const motion: number[] = [];
  for (let i = 1; i < samples.length; i++) {
    const prevLocal = bodyLocals[i - 1];
    const currLocal = bodyLocals[i];
    const prevVis = samples[i - 1].worldLandmarks;
    const currVis = samples[i].worldLandmarks;
    let sumWeighted = 0;
    let sumWeight = 0;
    for (let j = 0; j < prevLocal.length && j < currLocal.length; j++) {
      const weight = (prevVis[j].visibility ?? 0) * (currVis[j].visibility ?? 0);
      sumWeighted += prevLocal[j].distanceTo(currLocal[j]) * weight;
      sumWeight += weight;
    }
    motion.push(sumWeight > 1e-8 ? sumWeighted / sumWeight : 0);
  }

  const meanMotion = motion.reduce((a, b) => a + b, 0) / motion.length;
  const varianceMotion = motion.reduce((a, b) => a + (b - meanMotion) ** 2, 0) / motion.length;
  const stdMotion = Math.sqrt(varianceMotion);
  // 変動係数（標準偏差 / 平均）が小さければ、区間ごとの動きに有意な差がないと判断する。
  if (meanMotion < 1e-8 || stdMotion / meanMotion < 0.15) return samples;

  const visibility = samples.map((s) => averageVisibility(s.landmarks2d));
  const maxMotion = Math.max(...motion);
  const maxVisibility = Math.max(...visibility, 1e-8);

  let bestStart = 0;
  let bestScore = -Infinity;
  for (let start = 0; start + seedSize <= samples.length; start++) {
    // ウィンドウ内の遷移（seedSize - 1 個）の平均動き量。
    let sumMotion = 0;
    for (let i = start; i < start + seedSize - 1; i++) sumMotion += motion[i];
    const avgMotion = sumMotion / (seedSize - 1);

    let sumVisibility = 0;
    for (let i = start; i < start + seedSize; i++) sumVisibility += visibility[i];
    const avgVisibility = sumVisibility / seedSize;

    const motionScore = 1 - avgMotion / maxMotion; // 動きが少ないほど1に近い
    const visibilityScore = avgVisibility / maxVisibility; // visibilityが高いほど1に近い
    const score = motionScore * 0.7 + visibilityScore * 0.3;
    if (score > bestScore) {
      bestScore = score;
      bestStart = start;
    }
  }

  // シード窓を、隣接遷移の動き量がシード窓自身の動き量の中央値ベースの
  // 閾値以下である限り前後へ伸ばす。全編静止していれば全サンプルまで伸びきる。
  const seedMotions = motion.slice(bestStart, bestStart + seedSize - 1);
  const expansionThreshold = median(seedMotions) * EXPANSION_MOTION_FACTOR;

  let left = bestStart;
  let right = bestStart + seedSize - 1; // サンプルの右端（inclusive）
  while (left > 0 && motion[left - 1] <= expansionThreshold) left--;
  while (right < samples.length - 1 && motion[right] <= expansionThreshold) right++;

  return samples.slice(left, right + 1);
}

/** 鮮明度スコアの中央値に対する除外しきい値の係数。 */
const SHARPNESS_MEDIAN_FACTOR = 0.4;
/** フィルタ後の残数がこれ未満になる場合は、入力が枯渇しすぎないよう除外せず元をそのまま返す。 */
const MIN_FRAMES_AFTER_SHARPNESS_FILTER = 3;

/**
 * 鮮明度スコア（sharpness、ラプラシアン分散）が中央値の SHARPNESS_MEDIAN_FACTOR 倍未満の
 * フレームを除外する。モーションブラーの強いフレームを多視点融合に混ぜないための前処理。
 * 除外後の残数が MIN_FRAMES_AFTER_SHARPNESS_FILTER 枚未満になる場合は除外せず元の配列を返す。
 */
export function filterSharpFrames(samples: PoseSample[]): PoseSample[] {
  if (samples.length === 0) return samples;
  const threshold = median(samples.map((s) => s.sharpness)) * SHARPNESS_MEDIAN_FACTOR;
  const filtered = samples.filter((s) => s.sharpness >= threshold);
  if (filtered.length < MIN_FRAMES_AFTER_SHARPNESS_FILTER) return samples;
  return filtered;
}

/**
 * 値でソートし、上下それぞれ TRIM_RATIO 割合を切り捨ててから、残りを重み付き平均する。
 * エントリ数が MIN_VIEWS_FOR_TRIM 未満のときは切り捨てず全件を使う。
 */
function trimmedWeightedAverage(entries: { value: number; weight: number }[]): number {
  const trim = entries.length >= MIN_VIEWS_FOR_TRIM;
  const sorted = [...entries].sort((a, b) => a.value - b.value);
  const trimCount = trim ? Math.floor(sorted.length * TRIM_RATIO) : 0;
  const kept = trimCount > 0 ? sorted.slice(trimCount, sorted.length - trimCount) : sorted;

  let sumValue = 0;
  let sumWeight = 0;
  for (const e of kept) {
    sumValue += e.value * e.weight;
    sumWeight += e.weight;
  }
  if (sumWeight > 1e-8) return sumValue / sumWeight;
  // 残った全ビューの信頼度がほぼ0だった場合の保険：単純平均で妥協する。
  return kept.reduce((sum, e) => sum + e.value, 0) / kept.length;
}

/**
 * 複数フレーム（視点）の MediaPipe world landmarks を1セットに統合する。
 * 基準ビュー（全33点の平均 visibility が最大のフレーム）の胴体フレームに、
 * 他のビューの胴体フレームを回転整列してから、ランドマーク・軸ごとに
 * visibility^2 で加重したトリム平均（上下 TRIM_RATIO を切り捨てた平均）を取る。
 * 1フレームの誤検出に引っ張られにくくするための処理で、ビュー数が少ないときは
 * 通常の加重平均にフォールバックする（trimmedWeightedAverage が対応する）。
 * ビューが1枚だけならそのまま素通しする。
 */
export function fusePoses(samples: PoseSample[]): FusedPose {
  if (samples.length === 0) {
    throw new Error('統合対象のフレームがありません');
  }

  let refIndex = 0;
  let refScore = -Infinity;
  samples.forEach((s, i) => {
    const score = averageVisibility(s.worldLandmarks);
    if (score > refScore) {
      refScore = score;
      refIndex = i;
    }
  });
  const ref = samples[refIndex];

  // 手は body のように多視点融合せず、サイドごとにスコア最良の1フレームをそのまま使う
  // （手の向きの融合は難しく、静止ポーズでは最良1フレームで十分なため）。
  const leftHand = selectBestHand(samples, 'leftHand');
  const rightHand = selectBestHand(samples, 'rightHand');

  if (samples.length === 1) {
    return {
      worldLandmarks: ref.worldLandmarks,
      refLandmarks2d: ref.landmarks2d,
      imageSize: { width: ref.imageWidth, height: ref.imageHeight },
      leftHand,
      rightHand,
    };
  }

  const refFrame = computeTorsoFrame(ref.worldLandmarks);

  // 各ビューを基準フレームへ回転整列した world landmarks を集める。
  const alignedViews: Landmark[][] = samples.map((sample) => {
    if (sample === ref) return ref.worldLandmarks;
    const viewFrame = computeTorsoFrame(sample.worldLandmarks);
    // R = frameRef * frameView^T : このビューの姿勢を基準ビューの姿勢へ回転させる行列。
    const viewFrameT = viewFrame.clone().transpose();
    const rot = refFrame.clone().multiply(viewFrameT);
    return sample.worldLandmarks.map((lm) => {
      const v = toVec3(lm).applyMatrix3(rot);
      return { x: v.x, y: v.y, z: v.z, visibility: lm.visibility ?? 0 };
    });
  });

  const fused: Landmark[] = [];
  for (let i = 0; i < LANDMARK_COUNT; i++) {
    const entries = alignedViews.map((view) => {
      const lm = view[i];
      const vis = lm.visibility ?? 0;
      return { x: lm.x, y: lm.y, z: lm.z, vis, weight: vis * vis };
    });
    const sumWeight = entries.reduce((sum, e) => sum + e.weight, 0);

    if (sumWeight > 1e-8) {
      const x = trimmedWeightedAverage(entries.map((e) => ({ value: e.x, weight: e.weight })));
      const y = trimmedWeightedAverage(entries.map((e) => ({ value: e.y, weight: e.weight })));
      const z = trimmedWeightedAverage(entries.map((e) => ({ value: e.z, weight: e.weight })));
      const sumVis = entries.reduce((sum, e) => sum + e.vis * e.weight, 0);
      fused.push({ x, y, z, visibility: sumVis / sumWeight });
    } else {
      // どのビューでも信頼度がほぼ0だった点は基準ビューの値をそのまま使う。
      const lm = ref.worldLandmarks[i];
      fused.push({ x: lm.x, y: lm.y, z: lm.z, visibility: lm.visibility ?? 0 });
    }
  }

  return {
    worldLandmarks: fused,
    refLandmarks2d: ref.landmarks2d,
    imageSize: { width: ref.imageWidth, height: ref.imageHeight },
    leftHand,
    rightHand,
  };
}
